import React from 'react';

import realContactSmall from '@assets/img/icons/real-contact-small.svg';
import { contactStrings } from '@data/contacts';
import { Company } from '@models/Company';
import { Contact, ContactType } from '@models/next/Contact';
import { User } from '@models/User';
import { makeMuiStyles, useCommonStyles, useIsMobile } from '@styles';
import { createStrings } from '@utils/local';
import clsx from 'clsx';
import Color from 'color';
import { Link, useHistory } from 'react-router-dom';

const strings = createStrings({
  ja: {
    edit: '編集',
    realContactTip: 'D2D MEMBERS連携済み',
    myself: '御社',
  },
});

export interface ContactCardProps {
  editUrl?: string;
  detailsUrl?: string;
  contact: Contact;
  user: User;
  company: Company;
  contactType: ContactType;
}

const useStyles = makeMuiStyles((theme, common) => ({
  container: {
    ...common.flexInline,
    alignItems: 'stretch',
    justifyContent: 'space-between',
    backgroundColor: theme.palette.grey[50],
    borderRadius: 2,
  },
  cardLinkDetails: {
    cursor: 'pointer',
  },
  bar: {
    backgroundColor: theme.palette.primary.main,
    minWidth: 11.67,
    minHeight: 140,
    borderTopLeftRadius: 2,
    borderBottomLeftRadius: 2,
  },
  content: {
    flex: 1,
    padding: 10,
    minHeight: 140,
    ...common.flexColumn,
    ...common.justifyStart,
    width: '100%',
  },
  contentHead: {
    ...common.flexInline,
    height: 40,
    alignItems: 'center',
  },
  companyNameCol: {
    ...common.middleText,
    paddingLeft: 8,
    paddingRight: 8,
    color: theme.palette.text.primary,
  },
  editBtnCol: {
    flex: 1,
    paddingLeft: 8,
    paddingRight: 18,
    textAlign: 'end',
    ...common.middleText,
  },
  editBtn: {
    color: theme.palette.primary.main,
  },
  contentBody: {
    flex: 1,
    ...common.flexColumn,
    ...common.middleText,
    color: theme.palette.grey[800],
    width: '100%',
  },
  contentRow: {
    flex: 1,
    ...common.flexInline,
    marginBottom: 5,
    [theme.breakpoints.down('xs')]: {
      ...common.flexColumn,
      rowGap: '0.5rem',
    },
  },
  contentRowLeftCol: {
    width: '60%',
    ...common.flexInline,
    alignItems: 'center',
    paddingLeft: 8,
    paddingRight: 8,
  },
  textInline: {
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    overflow: 'hidden',
    wordWrap: 'normal',
    flexWrap: 'nowrap',
  },
  contactFieldEmpty: {
    color: theme.palette.grey[500],
  },
  realContact: {
    position: 'relative',
    top: 2,
    marginLeft: '1rem',
  },
  realContactTip: {
    ...common.textGray700,
    fontSize: 10,
    lineHeight: 1,
    pointerEvents: 'none',
    fontWeight: 100,
    position: 'absolute',
    left: '160%',
    top: -12,
    backgroundColor: Color(theme.palette.primary.main).alpha(0.1).toString(),
    padding: '14px 9px',
    whiteSpace: 'pre',
    zIndex: 1,
    boxShadow: '0px 4px 4px rgba(0, 0, 0, 0.25)',
    opacity: 0,
    '$realContact:hover &': {
      opacity: 1,
    },
  },
  contentRowRightCol: {
    flex: 1,
    ...common.flexInline,
    alignItems: 'center',
    paddingLeft: 8,
    paddingRight: 8,
  },
}));

export interface RealContactTipProps {
  isSelf?: boolean;
}

const RealContactTip: React.FC<RealContactTipProps> = (props) => {
  const { isSelf } = props;
  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  return (
    <div className={classes.realContactTip}>{isSelf ? strings.myself : strings.realContactTip}</div>
  );
};

export const ContactCard: React.FC<ContactCardProps> = (props) => {
  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  const { contact, editUrl, detailsUrl, company, contactType } = props;

  const {
    company_name,
    english_name,
    zip_code,
    principal_name,
    phone,
    address1,
    address2,
    email,
    sns_id,
    user: contactUser,
    company_id,
    social_credit_id,
  } = contact;

  const isRealUser = !!contactUser;
  const editable = !contactUser || company_id === company.id;
  const showSocialCreditId = contactType === 'shipper';

  const history = useHistory();
  const isMobile = useIsMobile();

  return (
    <div role="ContactCard" className={clsx(classes.container)}>
      <div className={classes.bar} />

      <div className={classes.content}>
        <div className={classes.contentHead}>
          <div
            className={clsx(classes.companyNameCol, detailsUrl && classes.cardLinkDetails)}
            onClick={() => {
              if (detailsUrl) {
                history.push(detailsUrl);
              }
            }}
          >
            {company_name}
            <span className={clsx(!english_name && classes.contactFieldEmpty)}>
              {' - '}
              {english_name || contactStrings.english_name}
            </span>
          </div>
          {isRealUser && (
            <div className={classes.realContact}>
              <img alt="Real Contact" src={realContactSmall} style={{ display: 'block' }} />
              {!isMobile && <RealContactTip isSelf={company_id === company.id} />}
            </div>
          )}
          {editable && (
            <div className={classes.editBtnCol}>
              <Link to={editUrl || '#'} className={classes.editBtn}>
                {strings.edit}
              </Link>
            </div>
          )}
        </div>
        <div
          className={clsx(classes.contentBody, detailsUrl && classes.cardLinkDetails)}
          onClick={() => {
            if (detailsUrl) {
              history.push(detailsUrl);
            }
          }}
        >
          {showSocialCreditId && (
            <div className={classes.contentRow}>
              <div
                className={clsx(
                  classes.contentRowLeftCol,
                  !social_credit_id && classes.contactFieldEmpty,
                )}
              >
                {social_credit_id || `${contactStrings.social_credit_id}...`}
              </div>
            </div>
          )}
          <div className={classes.contentRow}>
            <div
              className={clsx(classes.contentRowLeftCol, !zip_code && classes.contactFieldEmpty)}
            >
              {zip_code || `${contactStrings.zip_code}...`}
            </div>
            <div className={classes.contentRowRightCol}>
              <span className={classes.textInline}>
                {`${principal_name || ''}  ${phone || ''}`}
              </span>
            </div>
          </div>
          <div className={classes.contentRow}>
            <div
              className={clsx(classes.contentRowLeftCol, !address1 && classes.contactFieldEmpty)}
            >
              <span className={classes.textInline}>
                {address1 || `${contactStrings.zip_code}...`}
              </span>
            </div>
            <div className={clsx(classes.contentRowRightCol, !email && classes.contactFieldEmpty)}>
              <span className={classes.textInline}>{email || `${contactStrings.email}...`}</span>
            </div>
          </div>
          <div className={classes.contentRow}>
            <div
              className={clsx(classes.contentRowLeftCol, !address2 && classes.contactFieldEmpty)}
            >
              <span className={classes.textInline}>
                {address2 || `${contactStrings.address2}...`}
              </span>
            </div>
            <div className={clsx(classes.contentRowRightCol, !sns_id && classes.contactFieldEmpty)}>
              <span className={classes.textInline}>{sns_id || `${contactStrings.sns_id}...`}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
